// src/hooks/useAvatarUpload.ts
//
// Avatar upload helpers — the 3 client-side mitigations from
// docs/briefs/SECURITY_PLAN.md Phase 2:
//   2.2 MIME allowlist (jpeg / png / webp · NO svg)
//   2.3 Magic-byte sniff (catches `.html` renamed to `.jpg`)
//   2.4 5 MB size cap
//
// The 2.5 path structure (`avatars/<auth.uid()>/<uuid>.<ext>`) is built
// here; bucket-side RLS enforces that the first path segment matches the
// authenticated user. The 2.6 profiles.avatar_url shape check lives in
// migration 025 (trigger validate_profile_avatar_url).
//
// Pure helpers (validateFile · detectImageType) are exported separately
// so a Jest test can exercise them without spinning up Supabase.

import { useCallback, useState } from "react";
import { supabase } from "../lib/supabase";

// ---------------------------------------------------------------------------
// Public constants — surfaced in UI copy + tests
// ---------------------------------------------------------------------------

export const AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const AVATAR_ALLOWED_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export const AVATAR_ALLOWED_EXT_LABEL = "JPEG, PNG, or WEBP";
export const AVATAR_BUCKET = "avatars";

export type AvatarImageType = "jpeg" | "png" | "webp";

// ---------------------------------------------------------------------------
// Magic-byte detection
// ---------------------------------------------------------------------------
// Reads the first 12 bytes of `file` and matches against the known
// signature of each allowed format. Defends against MIME spoofing where
// an attacker renames `.html` → `.jpg` to slip past the type filter.
//
// Signatures:
//   JPEG : FF D8 FF
//   PNG  : 89 50 4E 47 0D 0A 1A 0A  ("\x89PNG\r\n\x1a\n")
//   WEBP : 52 49 46 46  __ __ __ __  57 45 42 50  ("RIFF....WEBP")

export function detectImageType(bytes: Uint8Array): AvatarImageType | null {
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xff &&
    bytes[1] === 0xd8 &&
    bytes[2] === 0xff
  ) {
    return "jpeg";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "png";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "webp";
  }
  return null;
}

async function readFirstBytes(file: File, length: number): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  const buf = await slice.arrayBuffer();
  return new Uint8Array(buf);
}

// ---------------------------------------------------------------------------
// validateFile — the 3 client-side checks
// ---------------------------------------------------------------------------

export type ValidateOk = { ok: true; type: AvatarImageType; ext: string };
export type ValidateFail = { ok: false; reason: string };
export type ValidateResult = ValidateOk | ValidateFail;

const TYPE_TO_EXT: Record<AvatarImageType, string> = {
  jpeg: "jpg",
  png: "png",
  webp: "webp",
};

const TYPE_TO_MIME: Record<AvatarImageType, string> = {
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export async function validateFile(file: File): Promise<ValidateResult> {
  // 2.4 size cap
  if (file.size > AVATAR_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      reason: `Too large (${mb} MB). Max is 5 MB.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, reason: "File is empty." };
  }

  // 2.2 MIME allowlist (declared type)
  if (!AVATAR_ALLOWED_MIMES.includes(file.type as (typeof AVATAR_ALLOWED_MIMES)[number])) {
    return {
      ok: false,
      reason: `This type isn't supported. Use ${AVATAR_ALLOWED_EXT_LABEL}.`,
    };
  }

  // 2.3 magic-byte sniff (defeats MIME spoofing)
  const bytes = await readFirstBytes(file, 12);
  const detected = detectImageType(bytes);
  if (detected === null) {
    return {
      ok: false,
      reason: `File bytes don't match an image. Pick a different file.`,
    };
  }

  // Sanity: declared MIME must match detected bytes
  if (TYPE_TO_MIME[detected] !== file.type) {
    return {
      ok: false,
      reason: `File extension and contents don't match. Pick a different file.`,
    };
  }

  return { ok: true, type: detected, ext: TYPE_TO_EXT[detected] };
}

// ---------------------------------------------------------------------------
// Path builder — enforces 2.5 path structure on the client
// ---------------------------------------------------------------------------

export function buildAvatarPath(userId: string, ext: string): string {
  // crypto.randomUUID is widely available in modern browsers + Node 19+.
  // Falls back to a Date.now + Math.random string if not present (very
  // old browsers — kept as a safety net, not a primary path).
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${userId}/${random}.${ext}`;
}

// ---------------------------------------------------------------------------
// Hook surface
// ---------------------------------------------------------------------------

export interface UseAvatarUploadResult {
  /** Upload `file` for `userId`, returns public URL on success. */
  uploadAvatar: (
    file: File,
    userId: string
  ) => Promise<{ ok: true; url: string } | { ok: false; reason: string }>;
  /** Remove the prior avatar object (if URL is from our bucket). */
  removeAvatar: (
    currentUrl: string | null,
    userId: string
  ) => Promise<{ ok: true } | { ok: false; reason: string }>;
  uploading: boolean;
}

export function useAvatarUpload(): UseAvatarUploadResult {
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = useCallback(
    async (file: File, userId: string) => {
      const validation = await validateFile(file);
      if (!validation.ok) {
        return { ok: false as const, reason: validation.reason };
      }

      const path = buildAvatarPath(userId, validation.ext);
      const contentType = TYPE_TO_MIME[validation.type];

      setUploading(true);
      try {
        const { error: uploadError } = await supabase.storage
          .from(AVATAR_BUCKET)
          .upload(path, file, {
            contentType,
            cacheControl: "3600",
            upsert: false, // unique uuid filename, no clobber
          });
        if (uploadError) {
          return {
            ok: false as const,
            reason: uploadError.message || "Couldn't upload right now.",
          };
        }

        const { data } = supabase.storage
          .from(AVATAR_BUCKET)
          .getPublicUrl(path);
        if (!data?.publicUrl) {
          return {
            ok: false as const,
            reason: "Upload succeeded but couldn't read public URL.",
          };
        }

        return { ok: true as const, url: data.publicUrl };
      } finally {
        setUploading(false);
      }
    },
    []
  );

  const removeAvatar = useCallback(
    async (currentUrl: string | null, userId: string) => {
      if (!currentUrl) {
        return { ok: true as const };
      }
      // Extract path from URL if it's one of ours.
      const marker = `/storage/v1/object/public/${AVATAR_BUCKET}/`;
      const idx = currentUrl.indexOf(marker);
      if (idx === -1) {
        // Not in our bucket (e.g. legacy external URL). Nothing to delete.
        return { ok: true as const };
      }
      const path = currentUrl.slice(idx + marker.length).split("?")[0];
      // Path must start with the user's own folder; defense-in-depth
      // against ever attempting a cross-user delete from this client.
      if (!path.startsWith(`${userId}/`)) {
        return {
          ok: false as const,
          reason: "Existing avatar belongs to a different user folder.",
        };
      }
      const { error } = await supabase.storage
        .from(AVATAR_BUCKET)
        .remove([path]);
      if (error) {
        return {
          ok: false as const,
          reason: error.message || "Couldn't remove the previous avatar.",
        };
      }
      return { ok: true as const };
    },
    []
  );

  return { uploadAvatar, removeAvatar, uploading };
}
